/**
 * Core Web Vitals capture (§13.6 targets: LCP ≤2.5 s, INP ≤200 ms, CLS ≤0.1 at p75 on mid-range Android).
 * PerformanceObserver-based, dependency-free, sampled per session, posted to `POST /v1/rum/vitals`
 * (docs/modules/infra.md api #7) when the page is hidden. No identifiers, no URLs with query strings — the
 * route pattern only.
 */
import type { WebVitalsBeacon } from './api/types';
import { getApi } from './api';
import { getRuntimeConfig } from './config';

type Metric = WebVitalsBeacon['name'];

const THRESHOLDS: Record<Metric, [good: number, poor: number]> = {
  LCP: [2500, 4000],
  INP: [200, 500],
  CLS: [0.1, 0.25],
  TTFB: [800, 1800],
};

export function rate(name: Metric, value: number): WebVitalsBeacon['rating'] {
  const [good, poor] = THRESHOLDS[name];
  return value <= good ? 'good' : value <= poor ? 'needs-improvement' : 'poor';
}

/** Sampling decision without `Math.random` (lint: client randomness is reserved for *.fx modules). */
export function shouldSample(sampleRate: number, random: () => number = cryptoUnit): boolean {
  if (sampleRate >= 1) return true;
  if (sampleRate <= 0) return false;
  return random() < sampleRate;
}

function cryptoUnit(): number {
  const c = globalThis.crypto;
  if (!c?.getRandomValues) return 1; // no entropy source → never sampled
  const buf = new Uint32Array(1);
  c.getRandomValues(buf);
  return (buf[0] ?? 0) / 2 ** 32;
}

interface LayoutShiftEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}
interface EventTimingEntry extends PerformanceEntry {
  interactionId?: number;
  processingStart: number;
}

export interface VitalsOptions {
  /** Current route pattern (e.g. `/l/:slug`), never a concrete URL. */
  route: () => string;
  send?: (beacon: WebVitalsBeacon) => void;
  sampleRate?: number;
  random?: () => number;
}

/** Pure aggregation used by `startVitals` and tests. */
export class VitalsCollector {
  lcp: number | null = null;
  ttfb: number | null = null;
  private clsSession = 0;
  private clsSessionStart = 0;
  private clsSessionLast = 0;
  cls = 0;
  private interactions = new Map<number, number>();

  recordLcp(startTime: number): void {
    this.lcp = startTime;
  }

  recordTtfb(responseStart: number): void {
    this.ttfb = responseStart;
  }

  /** web-vitals session-window rule: 1 s gap, 5 s max window; CLS is the largest window. */
  recordLayoutShift(e: Pick<LayoutShiftEntry, 'value' | 'hadRecentInput' | 'startTime'>): void {
    if (e.hadRecentInput) return;
    if (
      this.clsSession &&
      e.startTime - this.clsSessionLast < 1000 &&
      e.startTime - this.clsSessionStart < 5000
    ) {
      this.clsSession += e.value;
    } else {
      this.clsSession = e.value;
      this.clsSessionStart = e.startTime;
    }
    this.clsSessionLast = e.startTime;
    if (this.clsSession > this.cls) this.cls = this.clsSession;
  }

  recordInteraction(e: Pick<EventTimingEntry, 'interactionId' | 'duration'>): void {
    if (!e.interactionId) return;
    const prev = this.interactions.get(e.interactionId) ?? 0;
    if (e.duration > prev) this.interactions.set(e.interactionId, e.duration);
  }

  /** INP ≈ the 98th percentile of interaction latencies (one worst interaction per 50). */
  get inp(): number | null {
    if (!this.interactions.size) return null;
    const sorted = [...this.interactions.values()].sort((a, b) => b - a);
    const idx = Math.min(sorted.length - 1, Math.floor(sorted.length / 50));
    return sorted[idx] ?? null;
  }

  beacons(route: string, buildSha: string, navigationType?: string): WebVitalsBeacon[] {
    const out: WebVitalsBeacon[] = [];
    const push = (name: Metric, value: number | null) => {
      if (value == null) return;
      out.push({
        name,
        value: Math.round(value * 1000) / 1000,
        rating: rate(name, value),
        route,
        navigationType,
        buildSha,
      });
    };
    push('LCP', this.lcp);
    push('INP', this.inp);
    push('CLS', this.cls);
    push('TTFB', this.ttfb);
    return out;
  }
}

/** Starts observers; returns a stop function. A no-op when unsupported or not sampled. */
export function startVitals(opts: VitalsOptions): () => void {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return () => {};
  const cfg = getRuntimeConfig();
  if (!shouldSample(opts.sampleRate ?? cfg.vitalsSampleRate, opts.random)) return () => {};
  const send = opts.send ?? ((b: WebVitalsBeacon) => void getApi().postVitals(b, { beacon: true }));
  const collector = new VitalsCollector();
  const observers: PerformanceObserver[] = [];
  const observe = (
    type: string,
    cb: (entries: PerformanceEntryList) => void,
    extra: Record<string, unknown> = {},
  ) => {
    try {
      if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;
      const po = new PerformanceObserver((list) => cb(list.getEntries()));
      po.observe({ type, buffered: true, ...extra } as PerformanceObserverInit);
      observers.push(po);
    } catch {
      /* unsupported type */
    }
  };
  observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1];
    if (last) collector.recordLcp(last.startTime);
  });
  observe('layout-shift', (entries) => {
    for (const e of entries) collector.recordLayoutShift(e as LayoutShiftEntry);
  });
  observe(
    'event',
    (entries) => {
      for (const e of entries) collector.recordInteraction(e as EventTimingEntry);
    },
    { durationThreshold: 40 },
  );
  observe('navigation', (entries) => {
    const nav = entries[0] as PerformanceNavigationTiming | undefined;
    if (nav) collector.recordTtfb(nav.responseStart);
  });

  let sent = false;
  const report = () => {
    if (sent) return;
    sent = true;
    const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    for (const b of collector.beacons(opts.route(), cfg.buildSha, nav?.type)) send(b);
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') report();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', report);
  return () => {
    for (const po of observers) po.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', report);
  };
}
